import React, { useCallback, useMemo, useState } from 'react';
import { useApp, useAppDispatch } from '@context/AppContext';
import { useOnboardingTasks } from '@features/onboarding/hooks/useOnboardingTasks';
import { FlexView, Icon, IconButton, Loading, Menu, type MenuOption, Text } from '@goodfynd/ui';
import { addClassNames } from '@goodfynd/theme';
import { useAlertDialog } from '@goodfynd/ui';
import { useLocalStorageState } from '@hooks/useLocalStorageState';
import { useRouter } from 'next/router';

import type {
  OnboardingFeature,
  OnboardingTask,
  OnboardingWidgetProps,
} from './types';

import classes from './OnboardingWidget.module.css';

const STORAGE_KEY_COMPLETED_TASKS = 'onboarding-completed-tasks';

export function OnboardingWidget({ className }: OnboardingWidgetProps) {
  const router = useRouter();
  const { isOnboardingClosed, isOnboardingCollapsed } = useApp();
  const { setOnboardingClosed, setOnboardingCollapsed } = useAppDispatch();
  const openAlert = useAlertDialog();
  const [completedTasks, setCompletedTasks] = useLocalStorageState<string[]>(
    STORAGE_KEY_COMPLETED_TASKS,
    []
  );
  const [expandedFeatures, setExpandedFeatures] = useState<string[]>([]);

  // Defines onboarding features and their tasks
  const { features, isLoading } = useOnboardingTasks();

  const isTaskComplete = useCallback(
    (task: OnboardingTask) =>
      task.isComplete || (completedTasks ?? []).includes(task.id),
    [completedTasks]
  );

  // Calculate progress
  const { totalTasks, completedCount, progressPercent } = useMemo(() => {
    const allTasks = features.flatMap((f) => f.tasks);
    const total = allTasks.length;
    const completed = features
      .flatMap((f) => f.tasks)
      .filter((task) => isTaskComplete(task)).length;
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
    return {
      totalTasks: total,
      completedCount: completed,
      progressPercent: percent,
    };
  }, [features, isTaskComplete]);

  const isFeatureComplete = useCallback(
    (feature: OnboardingFeature) =>
      feature.tasks.every((task) => isTaskComplete(task)),
    [isTaskComplete]
  );

  const getFeatureProgress = useCallback(
    (feature: OnboardingFeature) => {
      const completed = feature.tasks.filter((task) =>
        isTaskComplete(task)
      ).length;
      return `${completed}/${feature.tasks.length}`;
    },
    [isTaskComplete]
  );

  const toggleFeature = useCallback((featureId: string) => {
    setExpandedFeatures((prev) =>
      prev.includes(featureId)
        ? prev.filter((id) => id !== featureId)
        : [...prev, featureId]
    );
  }, []);

  const markTaskComplete = useCallback(
    (taskId: string) => {
      setCompletedTasks((prev) => {
        const current = prev ?? [];
        if (current.includes(taskId)) {
          return current.filter((id) => id !== taskId);
        }
        return [...current, taskId];
      });
    },
    [setCompletedTasks]
  );

  const handleTaskClick = useCallback(
    (task: OnboardingTask) => {
      if (task.onClick) {
        task.onClick();
        return;
      }

      if (!task.href) {
        return;
      }

      if (task.href.startsWith('http')) {
        window.open(task.href, '_blank');
      } else {
        router.push(task.href);
      }
    },
    [router]
  );

  const getTaskMenuOptions = useCallback(
    (task: OnboardingTask): MenuOption[] => {
      if (!task.hasOptions) {
        return [];
      }

      const isComplete = isTaskComplete(task);
      return [
        {
          label: isComplete ? 'Mark as incomplete' : 'Mark as complete',
          onClick: () => markTaskComplete(task.id),
        },
      ];
    },
    [isTaskComplete, markTaskComplete]
  );

  const handleCollapse = useCallback(() => {
    setOnboardingCollapsed(!isOnboardingCollapsed);
  }, [isOnboardingCollapsed, setOnboardingCollapsed]);

  const handleClose = useCallback(() => {
    openAlert({
      title: 'Onboarding Guide Closed',
      message: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            You can always reopen the onboarding guide by clicking the{' '}
            <Icon
              name="info"
              size={16}
              style={{ display: 'inline-block', verticalAlign: 'middle' }}
            />{' '}
            icon button in the top right corner of the header, to the left of
            the help icon.
          </p>
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <img
              alt="Header with onboarding guide icon button"
              src="/images/onboarding/header-onboarding-icon.png"
              onError={(e) => {
                // Hide image if it doesn't exist
                (e.target as HTMLImageElement).style.display = 'none';
              }}
              style={{
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
              }}
            />
          </div>
        </div>
      ),
    });
    setOnboardingClosed(true);
  }, [openAlert, setOnboardingClosed]);

  // Don't render if closed
  if (isOnboardingClosed) {
    return null;
  }

  return (
    <div
      className={addClassNames(
        classes.container,
        { [classes.collapsed]: isOnboardingCollapsed },
        className
      )}
    >
      <div className={classes.header} onClick={handleCollapse}>
        <div className={classes['header-left']}>
          <Text as="subhead2" className={classes['header-title']}>
            Onboarding
          </Text>
        </div>
        <div className={classes['header-actions']}>
          <IconButton
            display="compact"
            icon={isOnboardingCollapsed ? 'chevron_up' : 'chevron_down'}
            size="small"
            tooltip={{ title: isOnboardingCollapsed ? 'Expand' : 'Collapse' }}
            onClick={(e) => {
              e.stopPropagation();
              handleCollapse();
            }}
          />
          <IconButton
            display="compact"
            icon="close"
            size="small"
            tooltip={{ title: 'Close' }}
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          />
        </div>
      </div>

      {isLoading && <Loading type="line" />}

      {!isOnboardingCollapsed && !isLoading && (
        <>
          <div className={classes['progress-container']}>
            <FlexView align="center" justify="space-between">
              <Text as="body2" className={classes['progress-text']}>
                {completedCount} of {totalTasks} tasks complete
              </Text>
              <Text as="body2" className={classes['progress-text']}>
                {progressPercent}%
              </Text>
            </FlexView>
            <div className={classes['progress-bar-wrapper']}>
              <div
                className={classes['progress-bar']}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <div className={classes.content}>
            {features.map((feature) => {
              const isExpanded = expandedFeatures.includes(feature.id);
              const featureComplete = isFeatureComplete(feature);

              return (
                <div className={classes['feature-accordion']} key={feature.id}>
                  <div
                    className={classes['feature-summary']}
                    onClick={() => toggleFeature(feature.id)}
                    style={{
                      pointerEvents: feature.busy ? 'none' : undefined,
                    }}
                  >
                    <div className={classes['feature-title']}>
                      {feature.busy ? (
                        <Loading size={16} type="circle" />
                      ) : (
                        <Icon
                          name={featureComplete ? 'check' : 'clear'}
                          size={16}
                          className={addClassNames(classes['task-icon'], {
                            [classes.complete]: featureComplete,
                            [classes.incomplete]: !featureComplete,
                          })}
                        />
                      )}

                      <span
                        className={addClassNames(
                          classes['feature-title-text'],
                          {
                            [classes.completed]: featureComplete,
                          }
                        )}
                      >
                        {feature.title}
                      </span>
                    </div>
                    <Text as="body2" className={classes['feature-progress']}>
                      {getFeatureProgress(feature)}
                    </Text>
                    <Icon
                      name="chevron_down"
                      size={16}
                      className={addClassNames(classes['feature-chevron'], {
                        [classes.expanded]: isExpanded,
                      })}
                    />
                  </div>

                  <div
                    className={addClassNames(classes['feature-tasks'], {
                      [classes.expanded]: isExpanded,
                    })}
                  >
                    <div className={classes['task-list']}>
                      {feature.tasks.map((task) => {
                        const taskComplete = isTaskComplete(task);

                        return (
                          <div
                            key={task.id}
                            onClick={() => handleTaskClick(task)}
                            className={addClassNames(classes['task-item'], {
                              [classes['has-actions']]:
                                !!getTaskMenuOptions(task).length,
                            })}
                            style={{
                              pointerEvents:
                                feature.busy || task.busy ? 'none' : undefined,
                            }}
                          >
                            <div className={classes['task-left']}>
                              {task.busy ? (
                                <Loading size={16} type="circle" />
                              ) : (
                                <Icon
                                  name={taskComplete ? 'check' : 'clear'}
                                  size={16}
                                  className={addClassNames(
                                    classes['task-icon'],
                                    {
                                      [classes.complete]: taskComplete,
                                      [classes.incomplete]: !taskComplete,
                                    }
                                  )}
                                />
                              )}

                              <span
                                className={addClassNames(
                                  classes['task-label'],
                                  {
                                    [classes.complete]: taskComplete,
                                  }
                                )}
                              >
                                {task.label}
                              </span>
                            </div>
                            <div
                              className={classes['task-actions']}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                display: !!getTaskMenuOptions(task).length
                                  ? undefined
                                  : 'none',
                              }}
                            >
                              <Menu
                                options={getTaskMenuOptions(task)}
                                anchor={
                                  <IconButton
                                    display="compact"
                                    icon="overflow_horizontal"
                                    size="small"
                                    title="More actions"
                                  />
                                }
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
